import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

interface AdminPaginationProps {
  total: number;
  page: number;
  pageSize: number;
  loading?: boolean;
  onPageChange: (page: number, pageSize: number) => void;
}

function toPositiveInteger(value: number, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

function getAdminPaginationItems(page: number, totalPages: number) {
  return Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter((item) => item === 1 || item === totalPages || Math.abs(item - page) <= 2)
    .reduce<(number | "ellipsis")[]>((items, item, index, visiblePages) => {
      if (index > 0 && item - visiblePages[index - 1] > 1) items.push("ellipsis");
      items.push(item);
      return items;
    }, []);
}

export default function AdminPagination({
  total,
  page,
  pageSize,
  loading = false,
  onPageChange,
}: AdminPaginationProps) {
  const { t } = useTranslation();
  const [jumpPage, setJumpPage] = useState("");
  const normalizedTotal = Math.max(0, Number(total) || 0);
  const normalizedPageSize = toPositiveInteger(pageSize, 20);
  const totalPages = Math.max(1, Math.ceil(normalizedTotal / normalizedPageSize));
  const currentPage = Math.min(toPositiveInteger(page, 1), totalPages);

  if (normalizedTotal <= normalizedPageSize) return null;

  const from = (currentPage - 1) * normalizedPageSize + 1;
  const to = Math.min(currentPage * normalizedPageSize, normalizedTotal);
  const pageItems = getAdminPaginationItems(currentPage, totalPages);
  const changePage = (nextPage: number) => {
    setJumpPage("");
    onPageChange(nextPage, normalizedPageSize);
  };
  const handleJump = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (jumpPage.trim() === "") return;

    const targetPage = Math.min(Math.max(toPositiveInteger(Number(jumpPage), 1), 1), totalPages);
    changePage(targetPage);
  };

  return (
    <div className="flex items-center justify-between px-1 py-3 mt-3">
      <span className="text-xs text-gray-400">
        {t("common.pagination.range", { from, to, total: normalizedTotal })}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={t("common.pagination.previous")}
          disabled={currentPage <= 1 || loading}
          onClick={() => changePage(currentPage - 1)}
          className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        {pageItems.map((item, index) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="px-1 text-xs text-gray-400">
              ...
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={t("common.pagination.page", { page: item })}
              aria-current={item === currentPage ? "page" : undefined}
              onClick={() => changePage(item)}
              className={`min-w-[28px] px-2 py-1 text-xs rounded-lg border cursor-pointer transition-colors ${
                item === currentPage
                  ? "text-white border-[#830051]"
                  : "text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              style={item === currentPage ? { backgroundColor: "#830051" } : undefined}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          aria-label={t("common.pagination.next")}
          disabled={currentPage >= totalPages || loading}
          onClick={() => changePage(currentPage + 1)}
          className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
        <form className="ml-2 flex items-center" onSubmit={handleJump}>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={totalPages}
            aria-label={t("common.pagination.pageNumber")}
            value={jumpPage}
            disabled={loading}
            onChange={(event) => setJumpPage(event.target.value)}
            className="w-12 rounded-lg border border-gray-300 px-1.5 py-1 text-center text-xs text-gray-600 outline-none transition-colors focus:border-[#830051] disabled:cursor-not-allowed disabled:opacity-40"
          />
        </form>
      </div>
    </div>
  );
}
